import { FC, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Pagination from "@mui/material/Pagination";
import SearchIcon from "@mui/icons-material/Search";
import { InputWidget } from "components/widgets/FormWidgets/InputWidget";
import { PhoneWidget } from "components/widgets/FormWidgets/PhoneWidget";
import { SelectWidget } from "components/widgets/FormWidgets/SelectWidget";

import { Loader } from "../../Loader/Loader";
import { useAppSelector, useAppThunkDispatch } from "../../../store";
import { TableComponent } from "../../TableComponent/TableComponent";
import { getClientsThunk } from "../../../store/features/thunk/clientThunk/clientThunk";

const headers: string[] = [
  "FIRST NAME",
  "LAST NAME",
  "CREATION DATE",
  "RECENT LOCATION",
  "DOCUMENTS COUNT",
  "RECENT DOCUMENTS",
];

const filterOptions = [
  { label: "Any fields", value: "any" },
  { label: "Specific fields", value: "specific" },
];

const perPageOptions = [
  { label: "20 clients", value: "20" },
  { label: "50 clients", value: "50" },
  { label: "100 clients", value: "100" },
];

export const Clients: FC<{ breadcrumbs?: boolean }> = () => {
  const [clientsFilter, setClientsFilter] = useState<Record<
    string,
    any
  > | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { clients } = useAppSelector(({ client }) => client);

  const navigate = useNavigate();
  const dispatchThunk = useAppThunkDispatch();

  const methods = useForm({
    defaultValues: { filterType: "any", perPageOption: "50" },
  });
  const { handleSubmit, watch } = methods;

  const currentFilterType = watch("filterType");
  const currentPerPageOption = watch("perPageOption");

  useEffect(() => {
    setPage(1);
    handleClients(1);
  }, [currentPerPageOption]);

  const handleClients = async (
    pageNumber: number = page,
    filter: Record<string, any> | null = clientsFilter,
  ) => {
    setIsLoading(true);

    const response = await dispatchThunk(
      getClientsThunk({
        clientsFilter: filter,
        page: pageNumber,
        perPage: Number(currentPerPageOption),
      }),
    );
    setTotalPages(response.payload.meta.totalPages);

    setIsLoading(false);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
    handleClients(value);
  };

  const onSubmit = (data: Record<string, any>) => {
    const { filterType, query, ...restFields } = data;

    let filter;
    if (filterType === "any") {
      filter = { query };
    } else {
      filter = restFields;
    }

    setClientsFilter({ filter });
    setPage(1);

    handleClients(1, { filter });
  };

  return (
    <>
      <Loader isLoading={isLoading} />

      <Box
        display="flex"
        justifyContent="space-between"
        gap="1rem"
        paddingBottom="30px"
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ width: "100%" }}
          >
            <Box
              display="grid"
              gridTemplateColumns="repeat(5, 1fr)"
              columnGap="1rem"
            >
              <SelectWidget
                label="Filter type"
                fieldId="filterType"
                type="select"
                option_list={filterOptions}
              />

              {currentFilterType === "any" ? (
                <Box gridArea="2 / 1 / 2 / 5">
                  <InputWidget
                    type="text"
                    fieldId="query"
                    label="Search by name, phone, or email"
                    value=""
                  />
                </Box>
              ) : (
                <>
                  <Box gridArea="2 / 1 / 2 / 2">
                    <InputWidget
                      type="text"
                      fieldId="firstName"
                      label="First name"
                      value=""
                    />
                  </Box>

                  <Box gridArea="2 / 2 / 2 / 3">
                    <InputWidget
                      type="text"
                      fieldId="lastName"
                      label="Last name"
                      value=""
                    />
                  </Box>

                  <Box gridArea="2 / 3 / 2 / 4">
                    <InputWidget
                      type="email"
                      fieldId="email"
                      label="Email"
                      value=""
                    />
                  </Box>

                  <Box gridArea="2 / 4 / 2 / 5">
                    <PhoneWidget
                      type="phone"
                      fieldId="phone"
                      label="Phone"
                      value=""
                      rules={{}}
                    />
                  </Box>
                </>
              )}

              <Button
                type="submit"
                variant="contained"
                color="success"
                sx={{
                  alignSelf: "center",
                  justifySelf: "start",
                  marginTop: "0.5rem",
                  gridArea: "2 / 5 / 2 / 6",
                }}
              >
                <SearchIcon />
              </Button>
            </Box>
          </form>
        </FormProvider>

        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box alignSelf="start" mt={2} width="12rem">
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                navigate("/client/newClient/info", { replace: true });
              }}
            >
              + Add new client
            </Button>
          </Box>

          <FormProvider {...methods}>
            <Box marginBottom="0.5rem">
              <SelectWidget
                label="Clients per page"
                fieldId="perPageOption"
                type="select"
                option_list={perPageOptions}
              />
            </Box>
          </FormProvider>
        </Box>
      </Box>

      <TableComponent
        type="clients"
        headers={headers}
        isEditDelete={false}
        rows={clients}
        onRowClick={(uuid) => {
          navigate(`/client/${uuid}/info`, { replace: true });
        }}
      />

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" m={2}>
          <Pagination
            page={page}
            count={totalPages}
            onChange={handlePageChange}
            showFirstButton
            showLastButton
            color="primary"
          />
        </Box>
      )}
    </>
  );
};
